'use client';

import { type FC, useContext, useMemo } from 'react';
import { usePathname, useParams } from 'next/navigation';
import { LeftNavItem, type LeftNavItemProps } from '@/_components/LeftNavItem';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';
import type { AdminEntityNavProps } from './AdminEntityNav.types';

export const AdminEntityNav: FC<AdminEntityNavProps> = ({ company }) => {
  const path = usePathname();
  const { companyHash } = useParams();
  const { preferences } = useContext(FirmSettingsContext);
  const { selectedCompanyConversations, hasAnyExportableDocs } =
    useContext(CompanyContext);
  const { user, permissions } = useContext(UserContext);

  const hasCompanyDocumentViewPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.view'],
    permissions,
  );

  const resolvedCompanyHash: string | undefined = useMemo(
    () => companyHash?.toString() || company?.hash_id,
    [companyHash, company?.hash_id],
  );

  const selectedConversationsUnseenCount: number = useMemo(
    () =>
      !!selectedCompanyConversations?.length &&
      Array.isArray(selectedCompanyConversations)
        ? selectedCompanyConversations?.reduce((total, conversation) => {
            const hasUnreadMessages =
              conversation.unseen_messages?.some(
                (m) => !m.is_log && m.unseen_user_ids.includes(user?.id!),
              ) ?? false;
            return total + (hasUnreadMessages ? 1 : 0);
          }, 0)
        : 0,
    [selectedCompanyConversations, user?.id],
  );

  const items: Array<LeftNavItemProps> = useMemo(
    () => [
      {
        name: 'Overview',
        icon: 'dashboard',
        href: '/',
        isActive: false,
      },
      {
        isSeparator: true,
      },
      {
        name: 'Back to Entities',
        icon: 'arrow-left',
        href: '/admin',
        isActive: false,
      },
      {
        isSeparator: true,
      },
      {
        name: 'Dashboard',
        icon: 'user-circle-gear',
        href: `/admin/${resolvedCompanyHash}`,
        isActive: path === `/admin/${resolvedCompanyHash}`,
      },
      ...(!preferences?.communication_disabled
        ? ([
            {
              name: 'Messages',
              icon: 'chats',
              href: `/admin/${resolvedCompanyHash}/messages`,
              isActive: path.startsWith(
                `/admin/${resolvedCompanyHash}/messages`,
              ),
              notificationCount: selectedConversationsUnseenCount,
              isDisabled: selectedCompanyConversations.length === 0,
              tooltipText: 'Entity has no active owners to message.',
            },
          ] as Array<LeftNavItemProps>)
        : []),
      {
        name: 'Documents',
        icon: 'files',
        href: `/admin/${resolvedCompanyHash}/documents`,
        isDisabled: !hasCompanyDocumentViewPermission,
        tooltipText: !hasCompanyDocumentViewPermission
          ? "You don't have permission to view documents"
          : undefined,
        isActive: path === `/admin/${resolvedCompanyHash}/documents`,
      },
      // Only show if connect is enabled and entity is an individual and has exportable docs
      ...(preferences?.review_and_export_enabled
        ? [
            {
              name: 'Connect',
              icon: 'plugs',
              href: `/admin/${resolvedCompanyHash}/connect`,
              isActive: path === `/admin/${resolvedCompanyHash}/connect`,
              isDisabled:
                company?.entity_type !== 'Individual' || !hasAnyExportableDocs,
              tooltipText:
                company?.entity_type !== 'Individual'
                  ? 'Connect is only available for individual entities'
                  : !hasAnyExportableDocs
                    ? 'No exportable documents found for this entity'
                    : undefined,
            } as LeftNavItemProps,
          ]
        : []),
    ],
    [
      path,
      resolvedCompanyHash,
      preferences,
      hasCompanyDocumentViewPermission,
      selectedCompanyConversations?.length,
      hasAnyExportableDocs,
      company?.entity_type,
      selectedConversationsUnseenCount,
    ],
  );

  return (
    <>
      {items.map((item, key) => (
        <LeftNavItem {...item} key={key} isActive={item.isActive} />
      ))}
    </>
  );
};
